/**
 * Project Management — role-based permissions.
 * Complements task-permissions.js for project-scoped workflows.
 */

import ability from '@/libs/acl/ability'
import { getEmpId, isMyTasksListTask } from './task-permissions'
import {
  PROJECT_ROLES,
  normalizeMembers,
  getMemberRole,
  normalizeProjectStatus,
  PROJECT_STATUSES,
  projectId,
} from './project-config'
import { normalizeTaskStatus, TASK_STATUSES } from './task-config'

function tryParseUserData() {
  try {
    return JSON.parse(localStorage.getItem('userData')) || {}
  } catch (e) {
    return {}
  }
}

export function isProjectMember(user, project) {
  const empId = getEmpId(user)
  if (!empId || !project) return false
  const creator = project.createdBy ?? project.created_by
  if (creator != null && String(creator) === String(empId)) return true
  return normalizeMembers(project.members).some(m => String(m.empId) === String(empId))
}

export function getUserProjectRole(user, project) {
  const empId = getEmpId(user)
  if (!empId || !project) return null
  const creator = project.createdBy ?? project.created_by
  if (creator != null && String(creator) === String(empId)) {
    return PROJECT_ROLES.PROJECT_MANAGER
  }
  return getMemberRole(project, empId)
}

export function canViewProject(user, project) {
  return isProjectMember(user, project)
}

export function canManageProject(user, project) {
  const role = getUserProjectRole(user, project)
  return role === PROJECT_ROLES.PROJECT_MANAGER
}

export function canManageProjectTeam(user, project) {
  return canManageProject(user, project)
}

/**
 * ACL-gated — mirrors `$can('create', 'task_management')` /
 * `$can('manage', 'project')` used in Task Management UI.
 */
export function canCreateProject() {
  return ability.can('create', 'task_management')
    || ability.can('manage', 'project')
    || ability.can('manage', 'task_management')
    || ability.can('manage', 'all')
}

export function canCreateTaskInProject(user, project) {
  if (!project) return true
  const role = getUserProjectRole(user, project)
  if (!role) return false
  if (normalizeProjectStatus(project) === PROJECT_STATUSES.ARCHIVED) return false
  return role === PROJECT_ROLES.PROJECT_MANAGER
    || role === PROJECT_ROLES.CREATOR
    || role === PROJECT_ROLES.CONTRIBUTOR
}

/**
 * Status transitions allowed by project role (when task belongs to a project).
 * Returns true when no project context — falls back to task-permissions.
 */
export function canChangeTaskStatusInProject(user, project, task, newStatus) {
  if (!project) return true
  const role = getUserProjectRole(user, project)
  if (!role) return false
  const current = normalizeTaskStatus(task)
  const next = normalizeTaskStatus({ status: newStatus })

  if (role === PROJECT_ROLES.PROJECT_MANAGER || role === PROJECT_ROLES.CREATOR) {
    return true
  }

  if (role === PROJECT_ROLES.REVIEWER) {
    if (next === TASK_STATUSES.REVIEW || next === TASK_STATUSES.DONE) return true
    if (current === TASK_STATUSES.REVIEW && next === TASK_STATUSES.IN_PROGRESS) return true
    return current !== TASK_STATUSES.REVIEW
  }

  if (role === PROJECT_ROLES.CONTRIBUTOR) {
    if (current === TASK_STATUSES.REVIEW) return false
    if (next === TASK_STATUSES.REVIEW) return true
    return next !== TASK_STATUSES.DONE
  }

  return false
}

function userHasAssignedTaskInProject(user, project, allTasks) {
  const empId = getEmpId(user)
  const pid = projectId(project)
  if (!empId || pid == null || !Array.isArray(allTasks)) return false
  return allTasks.some(task => {
    if (task?.isDeleted) return false
    const taskPid = task.projectId ?? task.project_id
    if (taskPid == null || String(taskPid) !== String(pid)) return false
    return isMyTasksListTask(user, task)
  })
}

/**
 * Stub projects from task rows when `/api/pm/projects` is empty or the user
 * is not a member — keeps assignees able to filter by project in the sidebar.
 */
export function inferProjectsFromTasks(allProjects, allTasks, user) {
  const base = Array.isArray(allProjects) ? [...allProjects] : []
  const u = user || tryParseUserData()
  const seen = new Set(base.map(p => String(projectId(p))).filter(Boolean))
  const inferred = []

  for (const task of allTasks || []) {
    if (task?.isDeleted) continue
    if (!isMyTasksListTask(u, task)) continue
    const pid = task.projectId ?? task.project_id
    if (pid == null) continue
    const key = String(pid)
    if (seen.has(key)) continue
    seen.add(key)
    inferred.push({
      id: pid,
      name: task.projectName || task.project_name || `Project ${pid}`,
      members: [],
      inferredFromTask: true,
    })
  }

  return inferred.length ? [...base, ...inferred] : base
}

export function visibleProjects(allProjects, user, allTasks) {
  if (!Array.isArray(allProjects)) return []
  const u = user || tryParseUserData()
  return allProjects.filter(p => canViewProject(u, p) || userHasAssignedTaskInProject(u, p, allTasks))
}

export function creatableProjects(allProjects, user, allTasks) {
  return visibleProjects(allProjects, user, allTasks).filter(p => canCreateTaskInProject(user, p))
}

/** Statuses the user may transition TO from the task's current status (project role aware). */
export function filterAllowedStatusOptions(user, project, task, allStatusOptions) {
  if (!Array.isArray(allStatusOptions)) return []
  if (!project) return allStatusOptions
  const allowed = allStatusOptions.filter(opt => canChangeTaskStatusInProject(
    user,
    project,
    task,
    opt.key ?? opt,
  ))
  const current = normalizeTaskStatus(task)
  if (!allowed.some(opt => (opt.key ?? opt) === current)) {
    const currentOpt = allStatusOptions.find(opt => (opt.key ?? opt) === current)
    if (currentOpt) allowed.unshift(currentOpt)
  }
  return allowed
}

/** True when the user can move a project task to at least one other status. */
export function hasAllowedStatusTransition(user, project, task, allStatusOptions) {
  if (!project) return true
  const current = normalizeTaskStatus(task)
  return filterAllowedStatusOptions(user, project, task, allStatusOptions)
    .some(opt => (opt.key ?? opt) !== current)
}

export function getProjectStatusDenialReason(user, project, task, newStatus) {
  if (!project) return null
  if (canChangeTaskStatusInProject(user, project, task, newStatus)) return null
  return 'Your project role does not allow this status change.'
}

/** Only the project PM may mark responsibilities done from the project panel. */
export function canToggleMemberResponsibility(user, project, memberEmpId) {
  if (!project || memberEmpId == null) return false
  return canManageProject(user, project)
}
